/**
 * AccountHold.ts
 * Full-screen hold resolution page shown to a business that has been
 * suspended by an admin. The business can review the hold reason,
 * select which commitments they're willing to make, upload supporting
 * documents, and submit their response.
 *
 * Store: window.__preuve_holds  →  Record<businessId, HoldRecord>
 */

// ─── Types ────────────────────────────────────────────────────────────────────

export type HoldStatus = 'active' | 'responded' | 'resolved' | 'lifted';

export interface HoldRequirement {
  key: string;
  label: string;
  icon: string;
  description: string;
}

export interface HoldDocument {
  id: string;
  requirementKey: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  dataUri: string;
  uploadedAt: string;
}

export interface HoldResponse {
  offerings: string[];           // requirement keys the business agreed to
  message: string;
  documents: HoldDocument[];
  submittedAt: string;
}

export interface HoldRecord {
  id: string;
  businessId: string;
  businessName: string;
  reason: string;
  requirements: string[];        // subset of ALL_REQUIREMENTS keys that admin requires
  deadline?: string;             // ISO date string
  createdAt: string;
  status: HoldStatus;
  response?: HoldResponse;
  resolvedAt?: string;
  adminNote?: string;
  liftedBy?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

export const ALL_REQUIREMENTS: HoldRequirement[] = [
  {
    key: 'gov_id',
    label: 'Government-Issued Photo ID',
    icon: 'bi-person-vcard',
    description: "Driver's license, state ID, or equivalent national identity card.",
  },
  {
    key: 'passport',
    label: 'Passport',
    icon: 'bi-passport',
    description: 'Valid passport (data page clearly visible, no obstructions).',
  },
  {
    key: 'business_license',
    label: 'Business License / Registration',
    icon: 'bi-award',
    description: 'Current business license or certificate of incorporation.',
  },
  {
    key: 'proof_of_address',
    label: 'Proof of Address',
    icon: 'bi-house-check',
    description: 'Utility bill, bank statement, or lease agreement dated within 90 days.',
  },
  {
    key: 'bank_statement',
    label: 'Bank Statement / Financial Records',
    icon: 'bi-bank',
    description: 'Most recent 3-month bank statement showing business account activity.',
  },
  {
    key: 'service_agreement',
    label: 'Sign Digital Service Agreement',
    icon: 'bi-pen',
    description: 'Digitally acknowledge and sign the PREUVU terms of service.',
  },
  {
    key: 'video_call',
    label: 'Schedule Video Verification Call',
    icon: 'bi-camera-video',
    description: 'Live verification call with a PREUVU compliance officer.',
  },
  {
    key: 'payment',
    label: 'Submit Outstanding Payment',
    icon: 'bi-credit-card',
    description: 'Clear any outstanding balance or subscription arrears.',
  },
  {
    key: 'written_statement',
    label: 'Provide Written Statement',
    icon: 'bi-file-text',
    description: 'A signed written statement addressing the compliance concern.',
  },
  {
    key: 'tax_id',
    label: 'Tax Identification Number (EIN / SSN)',
    icon: 'bi-building',
    description: 'Federal EIN or SSN for sole proprietors — redact all but last 4 digits.',
  },
];

// ─── Window Store ─────────────────────────────────────────────────────────────

declare global {
  interface Window {
    __preuve_holds?: Record<string, HoldRecord>;
  }
}

function getStore(): Record<string, HoldRecord> {
  if (!window.__preuve_holds) window.__preuve_holds = {};
  return window.__preuve_holds;
}

// ─── Exported CRUD helpers ────────────────────────────────────────────────────

export function getHoldRecord(businessId: string): HoldRecord | undefined {
  return getStore()[businessId];
}

export function getActiveHold(businessId: string): HoldRecord | undefined {
  const r = getStore()[businessId];
  return r && (r.status === 'active' || r.status === 'responded') ? r : undefined;
}

export function createHoldRecord(
  businessId: string,
  businessName: string,
  reason: string,
  requirements: string[],
  deadline?: string,
  adminNote?: string,
): HoldRecord {
  const record: HoldRecord = {
    id: `hold_${Date.now()}`,
    businessId,
    businessName,
    reason,
    requirements,
    deadline,
    createdAt: new Date().toISOString(),
    status: 'active',
    adminNote,
  };
  getStore()[businessId] = record;
  return record;
}

export function updateHoldRecord(
  businessId: string,
  patch: Partial<HoldRecord>,
): HoldRecord | null {
  const store = getStore();
  if (!store[businessId]) return null;
  store[businessId] = { ...store[businessId], ...patch };
  return store[businessId];
}

export function liftHold(businessId: string, liftedBy = 'admin'): boolean {
  const store = getStore();
  if (!store[businessId]) return false;
  store[businessId].status = 'lifted';
  store[businessId].liftedBy = liftedBy;
  store[businessId].resolvedAt = new Date().toISOString();
  return true;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch { return iso; }
}

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

function esc(s: string): string {
  return s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}

function reqByKey(key: string): HoldRequirement {
  return ALL_REQUIREMENTS.find(r => r.key === key) ?? {
    key, label: key, icon: 'bi-question-circle', description: '',
  };
}

// ─── Main Renderer ────────────────────────────────────────────────────────────

/**
 * renderAccountHold
 * Renders the full-screen hold resolution interface into `container`.
 * `navigate` is called with the route string when the user wants to go
 * somewhere after submitting their response (e.g. back to dashboard).
 */
export function renderAccountHold(
  container: HTMLElement,
  businessId: string,
  businessName: string,
  navigate: (route: string) => void,
): void {
  const store   = getStore();
  let   hold    = store[businessId];

  // ── If no hold record exists yet, show a "no active hold" state ────────────
  if (!hold) {
    container.innerHTML = `
      <div class="hold-wrap">
        <div class="hold-card">
          <div class="hold-icon-wrap hold-icon-ok">
            <i class="bi bi-check-circle-fill"></i>
          </div>
          <h2 class="hold-title">No Active Hold</h2>
          <p class="hold-sub">Your account is in good standing. No action is required at this time.</p>
          <button class="btn btn-primary" id="hold-back-btn">
            <i class="bi bi-arrow-left me-1"></i>Back to Dashboard
          </button>
        </div>
      </div>`;
    container.querySelector('#hold-back-btn')?.addEventListener('click', () => navigate('dashboard'));
    return;
  }

  // ── If hold is already lifted ──────────────────────────────────────────────
  if (hold.status === 'lifted') {
    container.innerHTML = `
      <div class="hold-wrap">
        <div class="hold-card">
          <div class="hold-icon-wrap hold-icon-ok">
            <i class="bi bi-unlock-fill"></i>
          </div>
          <h2 class="hold-title">Account Reinstated</h2>
          <p class="hold-sub">Your account hold has been lifted${hold.liftedBy ? ` by ${esc(hold.liftedBy)}` : ''}
            ${hold.resolvedAt ? ` on ${fmtDate(hold.resolvedAt)}` : ''}.</p>
          <button class="btn btn-primary" id="hold-back-btn">
            <i class="bi bi-house me-1"></i>Go to Dashboard
          </button>
        </div>
      </div>`;
    container.querySelector('#hold-back-btn')?.addEventListener('click', () => navigate('dashboard'));
    return;
  }

  // ── If response already submitted — show confirmation ─────────────────────
  if (hold.status === 'responded') {
    renderRespondedState(container, hold, navigate);
    return;
  }

  // ── Active hold — render response form ────────────────────────────────────
  renderActiveHold(container, hold, businessId, navigate, (updated) => {
    hold = updated;
  });
}

// ─── Responded State ──────────────────────────────────────────────────────────

function renderRespondedState(
  container: HTMLElement,
  hold: HoldRecord,
  navigate: (route: string) => void,
): void {
  const resp = hold.response!;
  container.innerHTML = `
    <div class="hold-wrap">
      <div class="hold-card hold-card-wide">
        <div class="hold-icon-wrap hold-icon-pending">
          <i class="bi bi-hourglass-split"></i>
        </div>
        <h2 class="hold-title">Response Submitted</h2>
        <p class="hold-sub">
          Your response was received on <strong>${fmtDate(resp.submittedAt)}</strong>.
          A PREUVU compliance officer will review your submission and contact you shortly.
        </p>

        <div class="hold-summary-box">
          <h4 class="hold-section-title"><i class="bi bi-list-check me-1"></i>Commitments Made</h4>
          <ul class="hold-check-list">
            ${resp.offerings.map(k => {
              const r = reqByKey(k);
              return `<li><i class="bi bi-check2-circle text-gold me-2"></i>${esc(r.label)}</li>`;
            }).join('')}
          </ul>
          ${resp.message ? `
            <h4 class="hold-section-title mt-3"><i class="bi bi-chat-text me-1"></i>Your Message</h4>
            <blockquote class="hold-message-quote">${esc(resp.message)}</blockquote>
          ` : ''}
          ${resp.documents.length > 0 ? `
            <h4 class="hold-section-title mt-3"><i class="bi bi-files me-1"></i>Documents Uploaded (${resp.documents.length})</h4>
            <ul class="hold-doc-list">
              ${resp.documents.map(d => `
                <li>
                  <i class="bi ${d.mimeType.startsWith('image/') ? 'bi-image' : 'bi-file-earmark-pdf'} me-2"></i>
                  ${esc(d.fileName)} <span class="hold-doc-size">${fmtSize(d.fileSize)}</span>
                </li>`).join('')}
            </ul>
          ` : ''}
        </div>

        <div class="hold-actions">
          <button class="btn btn-outline-secondary" id="hold-home-btn">
            <i class="bi bi-house me-1"></i>Return to Dashboard
          </button>
        </div>
      </div>
    </div>`;

  container.querySelector('#hold-home-btn')?.addEventListener('click', () => navigate('dashboard'));
}

// ─── Active Hold Form ─────────────────────────────────────────────────────────

function renderActiveHold(
  container: HTMLElement,
  hold: HoldRecord,
  businessId: string,
  navigate: (route: string) => void,
  onUpdate: (h: HoldRecord) => void,
): void {
  // Determine which requirements the admin specified; fallback = all requirements
  const required = hold.requirements.length > 0
    ? hold.requirements.map(k => reqByKey(k))
    : ALL_REQUIREMENTS;

  // Track state locally
  const selectedKeys  = new Set<string>();
  const uploadedDocs: HoldDocument[] = [];

  container.innerHTML = `
    <div class="hold-wrap">
      <div class="hold-card hold-card-wide">

        <!-- ── Header ──────────────────────────────────────────────────────── -->
        <div class="hold-header">
          <div class="hold-icon-wrap hold-icon-warn">
            <i class="bi bi-shield-exclamation"></i>
          </div>
          <div>
            <h2 class="hold-title">Account On Hold</h2>
            <p class="hold-sub mb-0">
              Your account <strong>${esc(hold.businessName)}</strong> has been temporarily suspended
              by a PREUVU compliance officer.
              ${hold.deadline ? `<span class="hold-deadline"><i class="bi bi-calendar-x me-1"></i>Response required by <strong>${fmtDate(hold.deadline)}</strong></span>` : ''}
            </p>
          </div>
        </div>

        <!-- ── Reason ───────────────────────────────────────────────────────── -->
        <div class="hold-reason-box">
          <div class="hold-reason-label"><i class="bi bi-exclamation-triangle-fill me-2"></i>Reason for Hold</div>
          <p class="hold-reason-text">${esc(hold.reason)}</p>
          ${hold.adminNote ? `<p class="hold-admin-note"><i class="bi bi-info-circle me-1"></i>${esc(hold.adminNote)}</p>` : ''}
        </div>

        <!-- ── Step 1: Select commitments ───────────────────────────────────── -->
        <div class="hold-step">
          <div class="hold-step-num">1</div>
          <div class="hold-step-body">
            <h3 class="hold-step-title">Select What You Are Willing to Provide</h3>
            <p class="hold-step-desc">Choose everything that applies. The more you provide, the faster your account will be reinstated.</p>
            <div class="hold-req-grid" id="hold-req-grid">
              ${required.map(r => `
                <label class="hold-req-card" data-key="${esc(r.key)}" id="req-card-${esc(r.key)}">
                  <input type="checkbox" class="hold-req-cb" value="${esc(r.key)}" />
                  <div class="hold-req-icon"><i class="bi ${esc(r.icon)}"></i></div>
                  <div class="hold-req-info">
                    <div class="hold-req-name">${esc(r.label)}</div>
                    <div class="hold-req-desc">${esc(r.description)}</div>
                  </div>
                  <div class="hold-req-check"><i class="bi bi-check-circle-fill"></i></div>
                </label>`).join('')}
            </div>
          </div>
        </div>

        <!-- ── Step 2: Upload documents ──────────────────────────────────────── -->
        <div class="hold-step">
          <div class="hold-step-num">2</div>
          <div class="hold-step-body">
            <h3 class="hold-step-title">Upload Supporting Documents <span class="hold-optional">(optional)</span></h3>
            <p class="hold-step-desc">Accepted: JPG, PNG, GIF, WebP, PDF. Maximum 10 MB per file.</p>

            <div class="doc-upload-zone" id="hold-drop-zone" role="button" tabindex="0"
                 aria-label="Drop files here or click to upload">
              <i class="bi bi-cloud-arrow-up" style="font-size:2.2rem;color:var(--brand-gold);margin-bottom:8px;"></i>
              <span class="doc-upload-primary">Drop files here or <u>browse</u></span>
              <span class="doc-upload-secondary">Passport, Government ID, Business License, etc.</span>
              <input type="file" id="hold-file-input" multiple accept="image/*,.pdf" style="display:none" />
            </div>

            <div class="hold-doc-grid" id="hold-doc-grid"></div>
          </div>
        </div>

        <!-- ── Step 3: Message ──────────────────────────────────────────────── -->
        <div class="hold-step">
          <div class="hold-step-num">3</div>
          <div class="hold-step-body">
            <h3 class="hold-step-title">Your Message <span class="hold-optional">(optional)</span></h3>
            <p class="hold-step-desc">Provide any additional context or explanation to help expedite your review.</p>
            <textarea id="hold-message" class="form-control" rows="4"
              placeholder="Explain your situation or any extenuating circumstances…" style="resize:vertical;"></textarea>
          </div>
        </div>

        <!-- ── Submit ───────────────────────────────────────────────────────── -->
        <div class="hold-actions">
          <div id="hold-error-msg" class="hold-error" style="display:none;"></div>
          <button class="btn btn-gold btn-lg" id="hold-submit-btn" disabled>
            <i class="bi bi-send me-2"></i>Submit Response
          </button>
        </div>

        <p class="hold-footer-note">
          <i class="bi bi-lock-fill me-1"></i>
          Your documents are encrypted and only reviewed by PREUVU compliance officers.
          They will not be shared with third parties.
        </p>
      </div>
    </div>

    <!-- ── Document Preview Modal ──────────────────────────────────────────── -->
    <div class="modal-overlay" id="hold-doc-modal" style="display:none;">
      <div class="modal-box modal-box-lg">
        <div class="modal-header-row">
          <span id="hold-doc-modal-title" class="modal-title-text">Document Preview</span>
          <button class="modal-close-btn" id="hold-doc-modal-close"><i class="bi bi-x-lg"></i></button>
        </div>
        <div id="hold-doc-modal-body" style="padding:0;overflow:hidden;border-radius:0 0 var(--radius-md) var(--radius-md);"></div>
      </div>
    </div>`;

  // ── Wire up checkboxes ─────────────────────────────────────────────────────
  const grid = container.querySelector('#hold-req-grid')!;
  grid.querySelectorAll<HTMLInputElement>('.hold-req-cb').forEach(cb => {
    cb.addEventListener('change', () => {
      const card = cb.closest<HTMLElement>('.hold-req-card')!;
      if (cb.checked) {
        selectedKeys.add(cb.value);
        card.classList.add('selected');
      } else {
        selectedKeys.delete(cb.value);
        card.classList.remove('selected');
      }
      syncSubmitButton();
    });
  });

  // ── Wire up file upload ────────────────────────────────────────────────────
  const dropZone   = container.querySelector<HTMLElement>('#hold-drop-zone')!;
  const fileInput  = container.querySelector<HTMLInputElement>('#hold-file-input')!;
  const docGrid    = container.querySelector<HTMLElement>('#hold-doc-grid')!;

  dropZone.addEventListener('click', () => fileInput.click());
  dropZone.addEventListener('keydown', (e) => { if ((e as KeyboardEvent).key === 'Enter') fileInput.click(); });
  dropZone.addEventListener('dragover', (e) => { e.preventDefault(); dropZone.classList.add('dragover'); });
  dropZone.addEventListener('dragleave', () => dropZone.classList.remove('dragover'));
  dropZone.addEventListener('drop', (e) => {
    e.preventDefault();
    dropZone.classList.remove('dragover');
    const files = Array.from((e as DragEvent).dataTransfer?.files ?? []);
    files.forEach(f => ingestFile(f));
  });
  fileInput.addEventListener('change', () => {
    Array.from(fileInput.files ?? []).forEach(f => ingestFile(f));
    fileInput.value = '';
  });

  function ingestFile(file: File): void {
    const MAX = 10 * 1024 * 1024;
    const ALLOWED = ['image/jpeg','image/png','image/gif','image/webp','application/pdf'];
    if (!ALLOWED.includes(file.type)) { showError(`"${file.name}" is not an accepted file type.`); return; }
    if (file.size > MAX) { showError(`"${file.name}" exceeds the 10 MB limit.`); return; }

    const reader = new FileReader();
    reader.onload = () => {
      const doc: HoldDocument = {
        id:            `hdoc_${Date.now()}_${Math.random().toString(36).slice(2,7)}`,
        requirementKey: '',
        fileName:      file.name,
        fileSize:      file.size,
        mimeType:      file.type,
        dataUri:       reader.result as string,
        uploadedAt:    new Date().toISOString(),
      };
      uploadedDocs.push(doc);
      appendDocCard(doc);
      syncSubmitButton();
    };
    reader.readAsDataURL(file);
  }

  function appendDocCard(doc: HoldDocument): void {
    const card = document.createElement('div');
    card.className = 'hold-doc-upload-card';
    card.dataset.id = doc.id;

    const isImage = doc.mimeType.startsWith('image/');
    const thumb = isImage
      ? `<img src="${doc.dataUri}" alt="${esc(doc.fileName)}" class="hold-doc-thumb-img" />`
      : `<div class="hold-doc-thumb-pdf"><i class="bi bi-file-earmark-pdf-fill"></i></div>`;

    card.innerHTML = `
      <div class="hold-doc-thumb">${thumb}</div>
      <div class="hold-doc-info">
        <span class="hold-doc-name" title="${esc(doc.fileName)}">${esc(doc.fileName)}</span>
        <span class="hold-doc-size">${fmtSize(doc.fileSize)}</span>
      </div>
      <div class="hold-doc-actions">
        <button class="btn-icon" data-action="view" title="Preview"><i class="bi bi-eye"></i></button>
        <button class="btn-icon btn-icon-danger" data-action="remove" title="Remove"><i class="bi bi-trash3"></i></button>
      </div>`;

    card.querySelector('[data-action="view"]')?.addEventListener('click', () => openDocPreview(doc));
    card.querySelector('[data-action="remove"]')?.addEventListener('click', () => {
      const idx = uploadedDocs.findIndex(d => d.id === doc.id);
      if (idx !== -1) uploadedDocs.splice(idx, 1);
      card.remove();
      syncSubmitButton();
    });

    docGrid.appendChild(card);
  }

  // ── Document preview modal ─────────────────────────────────────────────────
  const docModal      = container.querySelector<HTMLElement>('#hold-doc-modal')!;
  const docModalTitle = container.querySelector<HTMLElement>('#hold-doc-modal-title')!;
  const docModalBody  = container.querySelector<HTMLElement>('#hold-doc-modal-body')!;

  function openDocPreview(doc: HoldDocument): void {
    docModalTitle.textContent = doc.fileName;
    docModalBody.innerHTML = doc.mimeType.startsWith('image/')
      ? `<img src="${doc.dataUri}" style="max-width:100%;max-height:70vh;display:block;margin:auto;" alt="${esc(doc.fileName)}" />`
      : `<iframe src="${doc.dataUri}" style="width:100%;height:70vh;border:0;" title="${esc(doc.fileName)}"></iframe>`;
    docModal.style.display = 'flex';
  }

  container.querySelector('#hold-doc-modal-close')?.addEventListener('click', () => {
    docModal.style.display = 'none';
    docModalBody.innerHTML = '';
  });
  docModal.addEventListener('click', (e) => {
    if (e.target === docModal) {
      docModal.style.display = 'none';
      docModalBody.innerHTML = '';
    }
  });

  // ── Error message ──────────────────────────────────────────────────────────
  const errorEl = container.querySelector<HTMLElement>('#hold-error-msg')!;
  function showError(msg: string): void {
    errorEl.textContent = msg;
    errorEl.style.display = 'block';
    setTimeout(() => { errorEl.style.display = 'none'; }, 5000);
  }

  // ── Submit button state ────────────────────────────────────────────────────
  const submitBtn = container.querySelector<HTMLButtonElement>('#hold-submit-btn')!;
  function syncSubmitButton(): void {
    submitBtn.disabled = selectedKeys.size === 0 && uploadedDocs.length === 0;
  }

  // ── Submit ─────────────────────────────────────────────────────────────────
  submitBtn.addEventListener('click', () => {
    const msgEl = container.querySelector<HTMLTextAreaElement>('#hold-message')!;

    if (selectedKeys.size === 0 && uploadedDocs.length === 0) {
      showError('Please select at least one commitment or upload a document before submitting.');
      return;
    }

    submitBtn.disabled = true;
    submitBtn.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span>Submitting…';

    // Simulate async submission (replace with real API call if needed)
    setTimeout(() => {
      const response: HoldResponse = {
        offerings:   Array.from(selectedKeys),
        message:     msgEl.value.trim(),
        documents:   [...uploadedDocs],
        submittedAt: new Date().toISOString(),
      };

      const updated = updateHoldRecord(businessId, {
        status: 'responded',
        response,
      });

      if (updated) {
        onUpdate(updated);
        renderRespondedState(container, updated, navigate);
      } else {
        showError('Something went wrong. Please refresh and try again.');
        submitBtn.disabled = false;
        submitBtn.innerHTML = '<i class="bi bi-send me-2"></i>Submit Response';
      }
    }, 800);
  });
}
